import { memo, useState, type FC } from 'react';
import { useCollapse } from 'react-collapsed';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { getDataTestId } from '@/js/libs/utils/get-data-test-id/index.ts';

import Icons from '../../Elements/Icons/index.ts';
import { Tag } from '../../Elements/Tag/index.tsx';
import { EXPANDING_DURATION_MS, EXPANDING_EASING } from './constants.ts';
import { Footer, Header, Section, Title, Wrapper } from './styled.ts';
import type { CollapsibleFieldGroupProps } from './types.ts';

const CollapsibleFieldGroupComponent: FC<CollapsibleFieldGroupProps> = memo(function CollapsibleFieldGroupComponent({
  className,
  children,
  title,
  error,
  bottomSpacer,
  tag,
  defaultExpanded: defaultExpandedProp = false,
  actions,
  expanded: expandedProp,
  onExpandedChange,
}) {
  const theme = useTheme();

  const [expandedState, setExpandedState] = useState(defaultExpandedProp);

  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : expandedState;

  const handleToggle = useCallbackRef(() => {
    const newExpanded = !expanded;

    if (!isControlled) {
      setExpandedState(newExpanded);
    }

    onExpandedChange?.(newExpanded);
  });

  const { getCollapseProps } = useCollapse({
    isExpanded: expanded,
    duration: EXPANDING_DURATION_MS,
    easing: EXPANDING_EASING,
  });

  return (
    <Wrapper className={className} error={error} bottomSpacer={bottomSpacer} data-testid='collapsible-field-group'>
      <Header onClick={handleToggle} data-testid={`${getDataTestId(title)}-collapsible-header`}>
        <Title data-testid='collapsible-title'>
          <span>{title}</span>
          {tag && <Tag title={tag} data-testid='tag' />}
          {error && <Icons.Warning color={theme.colors.critical100} data-testid='error-icon' />}
        </Title>
        <Icons.Chevron size={24} align={expanded ? 'top' : 'bottom'} data-testid='chevron-icon' />
      </Header>
      <div {...getCollapseProps()} data-testid='collapsible-content'>
        <Section>{children}</Section>
        {actions && <Footer>{actions}</Footer>}
      </div>
    </Wrapper>
  );
});

export const CollapsibleFieldGroup = Object.assign(CollapsibleFieldGroupComponent, {
  EXPANDING_DURATION_MS,
  EXPANDING_EASING,
});
